import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PriceAnalysisChartWrapper } from '../../../market-derivative/PriceAnalysisChartWrapper';
import { MarketBiddingVolumeChartWrapper } from '../../../market-derivative/MarketBiddingVolumeChartWrapper';
import { ConsumptionGenerationChartWrapper } from '../../../market-derivative/ConsumptionGenerationChartWrapper';
import { CoalStockPriceChartWrapper } from '../../../market-derivative/CoalStockPriceChartWrapper';
import { FullscreenModal } from '../../../../components/FullscreenModal';
import Loading from '../../../../components/Loading';
import { getLatestDataPriceForecastDate } from '../../../../Rest_api/RestApi';
import { formatDateForAPI } from '../../../Rajasthan/Utility/const';

// Helper to safely get dates from Redux
const useDerivativeDates = () => {
    const defaultDates = useSelector((state: any) => state.defaultDatesSetter.defaultDates);
    const derivativeDates = defaultDates["Derivative"]?.defaultRangeDate;

    if (derivativeDates) {
        if (Array.isArray(derivativeDates)) {
            // Handle if it's an array (Trend/Range sometimes differ)
            // But based on config it seems to be an object with startDate/endDate
            return {
                startDate: formatDateForAPI(derivativeDates[0]?.startDate),
                endDate: formatDateForAPI(derivativeDates[0]?.endDate)
            }
        }
        return {
            startDate: formatDateForAPI(derivativeDates.startDate),
            endDate: formatDateForAPI(derivativeDates.endDate)
        };
    }

    // Fallback
    const today = new Date();
    const prior = new Date();
    prior.setDate(today.getDate() - 7);
    return {
        startDate: prior.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
    };
};

export default function DerivativeAnalysis() {
    const { startDate, endDate } = useDerivativeDates();

    const [isLoading, setIsLoading] = useState(false);
    // Keeping local loading states if we want per-card loading, 
    // but simplified compared to original file for now unless wrappers expose loading props we need to hook into.
    // The wrappers take startDate/endDate and likely fetch their own data.

    const [fullscreenCard, setFullscreenCard] = useState<string | null>(null);
    const [fullscreenStartDate, setFullscreenStartDate] = useState<string>(startDate);
    const [fullscreenEndDate, setFullscreenEndDate] = useState<string>(endDate);
    const [maxDate, setMaxDate] = useState<string>(
        new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]
    );

    useEffect(() => {
        // Sync fullscreen dates when main dates change
        setFullscreenStartDate(startDate);
        setFullscreenEndDate(endDate);
    }, [startDate, endDate]);

    // Fetch latest date logic from original file
    useEffect(() => {
        const fetchLatestDate = async () => {
            try {
                const response = await getLatestDataPriceForecastDate();
                if (response && response.latest_date) {
                    setMaxDate(response.latest_date);
                }
            } catch (error) {
                console.error("Error fetching latest date:", error);
            }
        };
        fetchLatestDate();
    }, []);

    // Fullscreen handlers
    useEffect(() => {
        const handleFullscreenEvent = (event: CustomEvent) => {
            handleFullscreen(event.detail);
        };
        window.addEventListener('openFullscreen', handleFullscreenEvent as EventListener);
        return () => window.removeEventListener('openFullscreen', handleFullscreenEvent as EventListener);
    }, [startDate, endDate]); // Re-bind if dates change? Or just use current state. Using functional update in handler might be safer.

    const handleFullscreen = (cardId: string) => {
        setFullscreenCard(cardId);
        setFullscreenStartDate(startDate);
        setFullscreenEndDate(endDate);
    };

    const handleCloseFullscreen = () => {
        setFullscreenCard(null);
    };

    const handleFullscreenDateChange = (newStartDate: string, newEndDate: string) => {
        setFullscreenStartDate(newStartDate);
        setFullscreenEndDate(newEndDate);
    };

    const getCardTitle = (cardId: string) => {
        switch (cardId) {
            case 'card1': return 'Price Analysis';
            case 'card2': return 'DAM Bids and MCV';
            case 'card3': return 'Consumption & Generation';
            case 'card4': return 'Coal Stock & Price';
            default: return 'Chart';
        }
    };

    const renderFullscreenContent = (cardId: string) => {
        switch (cardId) {
            case 'card1':
                return <PriceAnalysisChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card2':
                return <MarketBiddingVolumeChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card3':
                return <ConsumptionGenerationChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card4':
                return <CoalStockPriceChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            default:
                return null;
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 w-full h-[calc(100vh-85px)]">
            {/* Top Left */}
            <div className="dash-card w-full h-full">
                <PriceAnalysisChartWrapper startDate={startDate} endDate={endDate} />
            </div>

            {/* Top Right */}
            <div className="dash-card w-full h-full">
                <MarketBiddingVolumeChartWrapper startDate={startDate} endDate={endDate} />
            </div>

            {/* Bottom Left */}
            <div className="dash-card w-full h-full">
                <ConsumptionGenerationChartWrapper startDate={startDate} endDate={endDate} />
            </div>

            {/* Bottom Right */}
            <div className="dash-card w-full h-full">
                <CoalStockPriceChartWrapper startDate={startDate} endDate={endDate} />
            </div>

            {/* Fullscreen Modal */}
            <FullscreenModal
                isOpen={fullscreenCard !== null}
                onClose={handleCloseFullscreen}
                title={fullscreenCard ? getCardTitle(fullscreenCard) : ''}
                showDateControls={true}
                startDate={fullscreenStartDate}
                endDate={fullscreenEndDate}
                onDateChange={handleFullscreenDateChange}
                maxDate={fullscreenCard === 'card1' ? maxDate : undefined}
            >
                {fullscreenCard && renderFullscreenContent(fullscreenCard)}
            </FullscreenModal>
        </div>
    );
}
